// ── Common Button Labels ───────────────────────────────────────────

export const BUTTON_LABELS = {
  CANCEL: 'Cancel',
  YES: 'Yes',
  NO: 'No',
  CONFIRM: 'Confirm',
  CLOSE: 'Close',
  SAVE: 'Save',
  CREATE: 'Create',
  UPDATE: 'Update',
  DELETE: 'Delete',
  ASSIGN: 'Assign',
  SUBMIT: 'Submit',
  BACK: 'Back',
  ADD: 'Add',
  EDIT: 'Edit',
  VIEW: 'View',
  CLEAR_FILTERS: 'Clear filters',
  /** Applies the filters a popover holds, and fetches with them. */
  SEARCH: 'Search',
  ADD_USER: 'Add User',
  CREATE_USER: 'Create User',
  DISABLE_2FA: 'Disable 2FA',
  ADD_CLIENT: 'Add Client',
  CREATE_CLIENT: 'Create Client',
  NEW_TICKET: 'New Ticket',
  CREATE_TICKET: 'Create Ticket',
  CREATE_PLAN: 'Create Plan',
  ASSIGN_PLAN: 'Assign Plan',
  ASSIGN_PLAN_FOR_CLIENTS: 'Assign Plan for Clients',
  UPGRADE_PLAN: 'Upgrade Plan',
  UPGRADE_USER_PLAN: 'Upgrade User Plan',
  ASSIGN_PLAN_FOR_CUSTOMER: 'Assign Plan to Customer',
  DEACTIVATE: 'Deactivate',
  REACTIVATE: 'Reactivate',
  RUN_BILLING: 'Run Billing Now',
  VIEW_INVOICE: 'View Invoice',
  BACK_TO_INVOICES: 'Back to Invoices',
  BACK_TO_CLIENTS: 'Back to Clients',
  BACK_TO_USERS: 'Back to Users',
  BACK_TO_INTEGRATIONS: 'Back to Integrations',
  BACK_TO_EVENT_LOGS: 'Back to Event Logs',
  BACK_TO_SUBSCRIPTIONS: 'Back to Subscriptions',
  GO_TO_DASHBOARD: 'Go to Dashboard',
  GO_TO_PREVIOUS_PAGE: 'Go to Previous Page',
  SAVE_CHANGES: 'Save Changes',
  UPDATE_PASSWORD: 'Update Password',
  GENERATE_STRONG_PASSWORD: 'Generate strong password',
  COPY_PASSWORD: 'Copy',
  PASSWORD_COPIED: 'Copied',
  SEND_INVITE: 'Send Invite',
  ADD_FOLLOW_UP: 'Add Follow-up',
  SAVE_NOTIFICATIONS: 'Save Notifications',
  SAVE_SECURITY: 'Save Security',
  SAVE_GENERAL: 'Save General',
  DOWNLOAD_PDF: 'Download PDF',
  MARK_AS_PAID: 'Mark as Paid',
  SIGN_IN: 'Sign in',
  SELECT_ALL: 'Select All',
  DESELECT_ALL: 'Deselect All',
  REASSIGN: 'Reassign',
  UPDATE_STATUS: 'Update Status',
  MARK_AS_CLOSED: 'Mark as Closed',
} as const;

// ── Common Page Titles ─────────────────────────────────────────────

export const PAGE_TITLES = {
  /** The client detail page as seen by the account that belongs to that client. */
  DETAILS: 'Details',
  CLIENT_DETAILS: 'Client Details',
  USER_DETAILS: 'User Details',
  INTEGRATION_DETAILS: 'Integration Details',
  TICKET_DETAILS: 'Ticket Details',
  SUBSCRIPTION_DETAILS: 'Subscription Details',
  USER_SUBSCRIPTION_DETAILS: 'User Subscription Details',
  CUSTOMER_SUBSCRIPTION_DETAILS: 'Customer Subscription Details',
  INVOICE_DETAILS: 'Invoice Details',
  EVENT_DETAILS: 'Event Details',
  ACCESS_DENIED: 'Access Denied',
  SIGN_IN: 'Sign in',
  TWO_FACTOR_AUTHENTICATION: 'Two-Factor Authentication',
} as const;

// ── Common Table Headers ───────────────────────────────────────────

export const TABLE_HEADERS = {
  NAME: 'Name',
  EMAIL: 'Email',
  USER_TYPE: 'Internal/External',
  ROLE: 'Role',
  STATUS: 'Status',
  ACTIONS: 'Action',
  CREATED_AT: 'Created',
  UPDATED_AT: 'Updated',
  CATEGORY: 'Category',
  PRIORITY: 'Priority',
  INTEGRATION: 'Integration',
  CREDENTIAL_NAME: 'Credential Name',
  CREDENTIAL_TYPE: 'Credential Type',
  CREDENTIALS: 'Credentials',
  USER: 'User',
  CLIENT: 'Client',
  USER_CLIENT: 'User / Client Name',
  RELATED_TO: 'Related To',
  AMOUNT: 'Amount',
  CURRENCY: 'Currency',
  INVOICE_NUMBER: 'Invoice Number #',
  INVOICE_DATE: 'Invoice Date',
  ISSUE_DATE: 'Issue Date',
  DUE_DATE: 'Due Date',
  PAID_AT: 'Paid At',
  SUBSCRIPTION_START_DATE: 'Subscription Start Date',
  SUBSCRIPTION_END_DATE: 'Subscription End Date',
  ONBOARDING_DATE: 'Created At',
  ONBOARDED: 'Onboarded',
  CUSTOMER: 'Customer',
  CLIENT_NAME: 'Client Name',
  SLUG: 'Slug',
  TICKET_NUMBER: 'Ticket Number #',
  SUBJECT: 'Subject',
  RAISED_BY: 'Raised By',
  ASSIGNED_TO: 'Assigned To',
  ATTACHMENTS: 'Attachments',
  UPDATE_STATUS: 'Update Status',
  TYPE: 'Type',
  EVENT_TYPE: 'Event Type',
  SOURCE: 'Source',
  MESSAGE: 'Message',
  TIMESTAMP: 'Timestamp',
  BILLING_TYPE: 'Billing Type',
  PRICE: 'Price',
  USER_NAME: 'User Name',
  PLAN_NAME: 'Plan Name',
  START_DATE: 'Start Date',
  RENEWAL_DATE: 'Renewal Date',
  PDF: 'PDF',
  PLAN: 'Plan',
  CONNECTED_DATE: 'Connected Date',
  TICKET_ID: 'Ticket ID',
  LAST_UPDATED: 'Last Updated',
  ACTION_COL: 'Action',
  DETAILS: 'Details',
  TIME: 'Time',
  PROJECT: 'Project',
} as const;

// ── Table Empty Messages ───────────────────────────────────────────

/** Shown inside a table body when a query returns no rows. */
export const TABLE_EMPTY_MESSAGES = {
  DEFAULT: 'No data available',
  CLIENT_TICKETS: 'No tickets found for this client.',
  USER_TICKETS: 'No tickets found for this user.',
  USER_INVOICES: 'No invoices found for this user.',
  USER_EVENT_LOGS: 'No event logs found for this user.',
  USER_CREDENTIALS: 'No credentials found for this user.',
  INVOICES: 'No invoices available.',
} as const;

// ── Common Empty State Messages ────────────────────────────────────

export const EMPTY_STATES = {
  NO_USERS: {
    title: 'No users found',
    description: 'There are no users associated with this client.',
  },
  NO_INTEGRATIONS: {
    title: 'No integrations found',
    description: 'There are no integrations connected for this client.',
  },
  NO_INTEGRATIONS_FOR_FILTER: {
    title: 'No integrations found',
    description: 'There are no integrations matching your current filters.',
  },
  NO_TICKETS: {
    title: 'No tickets found',
    description: 'There are no support tickets for this client.',
  },
  NO_INVOICES: {
    title: 'No invoices found',
    description: 'There are no invoices for this client.',
  },
  NO_EVENT_LOGS: {
    title: 'No event logs found',
    description: 'There are no event logs for this client.',
  },
  NO_CREDENTIALS: {
    title: 'Integration Credentials',
    description: 'Manage credentials for connected integrations.',
  },
  NO_FILTER_RESULTS: {
    title: 'No results found',
    description: 'There are no items matching your current filters.',
  },
  NO_FOLLOW_UPS: {
    title: 'No follow-ups found',
    description: 'There are no follow-ups added for this user.',
  },
  // Page-level empty states
  CLIENTS_NO_RESULTS: {
    title: 'No clients found',
    description: 'Try adjusting your search or filters to find what you are looking for.',
  },
  USERS_NO_RESULTS: {
    title: 'No users found',
    description: 'There are no users matching your current filters.',
  },
  TICKETS_NO_RESULTS: {
    title: 'No tickets found',
    description:
      'There are no support tickets matching your current filters. Try adjusting your search or create a new ticket.',
  },
  INTEGRATIONS_NO_RESULTS: {
    title: 'No integrations found',
    description: 'Try adjusting your search or filters.',
  },
  EVENT_LOGS_NO_RESULTS: {
    title: 'No event logs found',
    description: 'There are no event logs matching your current filters. Try adjusting your search criteria.',
  },
  PLANS_NO_RESULTS: {
    title: 'No plans found',
    description: 'Create your first subscription plan to get started.',
  },
  SUBSCRIPTIONS_NO_RESULTS: {
    title: 'No subscriptions found',
    description:
      'No subscriptions match the current filter. Try selecting a different client, user, or assign a new plan.',
  },
  INVOICES_NO_RESULTS: {
    title: 'No invoices found',
    description:
      'No invoices match the current filter. Try selecting a different client, user, or run billing to generate invoices.',
  },
  USER_SUBSCRIPTIONS_NO_RESULTS: {
    title: 'No user subscriptions',
    description: 'No subscriptions found for your organization.',
  },
  NO_USER_SUBSCRIPTIONS: {
    title: 'No user subscriptions',
    description: 'No users have been assigned plans under this client subscription yet.',
  },
  NO_ACTIVE_SUBSCRIPTION: {
    title: 'No Active Subscription',
    description: "You don't have an active subscription. Contact your administrator.",
  },
  NO_INVOICES_FOUND: {
    title: 'No Invoices Found',
    description: 'There are no billing invoices associated with your account at this time.',
  },
  NO_ACTIVITY_LOGS: {
    title: 'No activity recorded',
    description: 'There is no activity log data available for this integration yet.',
  },
  // Dashboard empty states
  DASHBOARD_NO_INTEGRATIONS: {
    title: 'No integrations found',
    description: 'There are no integrations connected yet.',
  },
  TICKET_NO_LOGS: {
    title: 'No activity yet',
    description: 'Nothing has happened on this ticket since it was raised.',
  },
  INVOICE_NO_LOGS: {
    title: 'No activity yet',
    description: 'Nothing has happened on this invoice since it was raised.',
  },
  ACTIVITY_LOG_FAILED: {
    title: 'Could not load the activity',
    description: 'The history could not be fetched just now. Try again in a moment.',
  },
  SUBSCRIPTION_NO_LOGS: {
    title: 'No activity yet',
    description: 'Nothing has happened on this subscription since it was created.',
  },
  DASHBOARD_NO_TICKETS: {
    title: 'No tickets found',
    description: 'There are no support tickets yet. Create a new ticket to get started.',
  },
  // Not found states
  CLIENT_NOT_FOUND: {
    title: 'Client Not Found',
    description: "The client you're looking for doesn't exist or may have been removed.",
  },
  USER_NOT_FOUND: {
    title: 'User Not Found',
    description: "The user you're looking for doesn't exist or may have been removed.",
  },
  SUBSCRIPTION_NOT_FOUND: {
    title: 'Subscription Not Found',
    description: "The subscription you're looking for doesn't exist or may have been removed.",
  },
  INVOICE_NOT_FOUND: {
    title: 'Invoice Not Found',
    description: "The invoice you're looking for doesn't exist or may have been removed.",
  },
  SOURCE_CONFIGS_NO_RESULTS: {
    title: 'No source configs found',
    description: 'No sync sources are configured yet, or none match your search. Add a config to get started.',
  },
  PROJECTS_NO_RESULTS: {
    title: 'No projects found',
    description: 'No projects have been created yet, or none match your search. Add a project to get started.',
  },
  ROLES_NO_RESULTS: {
    title: 'No roles found',
    description: 'No roles have been created yet, or none match your search. Add a role to get started.',
  },
  INTEGRATION_NOT_FOUND: 'Integration not found.',
  EVENT_LOG_NOT_FOUND: 'Event log not found.',
} as const;

// ── Common Toast Messages ──────────────────────────────────────────

export const TOAST_MESSAGES = {
  // General
  COPIED: 'Copied to clipboard',

  // Auth
  WELCOME_BACK: 'Welcome back!',
  LOGOUT_SUCCESS: 'Logged out successfully',
  INVALID_CREDENTIALS: 'Invalid email or password',
  INVALID_LOGIN_RESPONSE: 'Unable to complete login. Please try again or contact support.',
  SESSION_EXPIRED: 'Session expired. Please log in again.',
  SERVER_UNREACHABLE: 'Cannot reach the server. Check your network connection and try again.',
  SERVER_UNAVAILABLE: 'The server is temporarily unavailable. Please try again in a moment.',
  SERVER_ERROR: 'Something went wrong on the server. Please try again later.',
  REQUEST_TIMEOUT: 'The request timed out. Please try again.',
  TOO_MANY_ATTEMPTS: 'Too many login attempts. Please wait a moment and try again.',
  LOGIN_FAILED: 'Unable to log in. Please try again.',
  RESET_LINK_SENT: 'If an account exists for that email, a reset link has been sent.',
  RESET_LINK_FAILED: 'Failed to send reset link. Please try again.',
  PASSWORD_UPDATED: 'Password updated. You can now log in.',
  PASSWORD_UPDATE_FAILED: 'Failed to update password. Reset token may be invalid or expired.',
  INVALID_CODE: 'Invalid code. Please try again.',
  INVALID_PASSWORD: 'Invalid password. Please try again.',
  INVALID_6_DIGIT_CODE: 'Please enter a valid 6-digit code.',
  TWO_FA_ENABLED: 'Two-Factor Authentication enabled successfully!',
  TWO_FA_DISABLED: 'Two-Factor Authentication disabled successfully!',
  TWO_FA_SETUP_FAILED: 'Failed to initialize 2FA setup.',
  RECOVERY_CODES_COPIED: 'Recovery codes copied to clipboard.',
  KEY_COPIED: 'Key copied to clipboard.',

  // Users
  USER_CREATED: 'User created successfully',
  USER_UPDATED: 'User updated successfully',
  USER_UPDATE_FAILED: 'Failed to update user. Please try again.',
  CLIENT_CREATED: 'Client created successfully',
  CLIENT_CREATE_FAILED: 'Failed to create client. Please try again.',
  CLIENT_UPDATED: 'Client updated successfully',
  CLIENT_UPDATE_FAILED: 'Failed to update client. Please try again.',
  CLIENT_ACTIVATED: 'Client activated',
  CLIENT_DEACTIVATED: 'Client deactivated',
  USER_ACTIVATED: 'User activated',
  USER_DEACTIVATED: 'User deactivated',
  USER_CREATE_FAILED: 'Failed to create user. Please try again.',
  FOLLOWUP_ADDED: 'Follow-up added successfully',
  FOLLOWUP_UPDATED: 'Follow-up updated successfully',
  FOLLOWUP_ADD_FAILED: 'Failed to add follow-up',
  FOLLOWUP_UPDATE_FAILED: 'Failed to update follow-up',

  // Credentials
  USERNAME_COPIED: 'Username copied',
  // PASSWORD_COPIED: 'Password copied',
  CREDENTIALS_UPDATED: 'Credentials updated successfully',
  CREDENTIALS_CREATED: 'Credentials added successfully',
  CREDENTIALS_FAILED: 'Failed to save credentials',

  // Plans & Subscriptions
  PLAN_ASSIGNED: 'Plan assigned successfully',
  PLAN_ASSIGN_FAILED: 'Failed to assign plan',
  PLAN_CREATED: 'Plan created successfully',
  PLAN_UPDATED: 'Plan updated successfully',
  PLAN_CREATE_FAILED: 'Failed to create plan',
  PLAN_UPDATE_FAILED: 'Failed to update plan',
  PLAN_CHANGED: 'Plan changed successfully',
  PLAN_CHANGE_FAILED: 'Failed to change plan',
  SUBSCRIPTION_DEACTIVATED: 'Subscription deactivated successfully',
  SUBSCRIPTION_DEACTIVATE_FAILED: 'Failed to deactivate subscription',
  SUBSCRIPTION_REACTIVATED: 'Subscription reactivated successfully',
  SUBSCRIPTION_REACTIVATE_FAILED: 'Failed to reactivate subscription',

  // Invoices & Billing
  INVOICE_PAID: 'Invoice marked as paid',
  INVOICE_PAID_FAILED: 'Failed to mark invoice as paid',
  INVOICE_PDF_FAILED: 'Failed to load invoice PDF',
  INVOICE_PDF_UNAVAILABLE: 'PDF not available for this invoice',
  BILLING_RUN_FAILED: 'Failed to run billing',

  // Tickets
  TICKET_CREATED: 'Ticket created successfully',
  TICKET_CREATE_FAILED: 'Failed to create ticket. Please try again.',
  TICKET_STATUS_UPDATED: 'Ticket status updated successfully',
  TICKET_STATUS_FAILED: 'Failed to update ticket status. Please try again.',
  TICKET_REASSIGNED: 'Ticket reassigned successfully',
  TICKET_REASSIGN_FAILED: 'Failed to reassign ticket. Please try again.',
  TICKET_REOPENED_ON_REASSIGN: 'Ticket reassigned and reopened',
  TICKET_CLOSED: 'Ticket closed successfully',
  TICKET_CLOSE_FAILED: 'Failed to close ticket. Please try again.',
  MESSAGE_SEND_FAILED: 'Failed to send message',
  MESSAGE_DELETE_FAILED: 'Failed to delete message',

  // Profile
  PROFILE_UPDATED: 'Profile updated successfully',
  PROFILE_UPDATE_FAILED: 'Failed to update profile',
  PASSWORD_CHANGED: 'Password changed successfully',
  PASSWORD_CHANGE_FAILED: 'Failed to change password',

  // Source Config
  SOURCE_CONFIG_CREATED: 'Source config added successfully',
  SOURCE_CONFIG_CREATE_FAILED: 'Failed to add source config. Please try again.',
  SOURCE_CONFIG_LOAD_FAILED: 'Failed to load source configs.',
  SOURCE_CONFIG_UPDATED: 'Source config updated successfully',
  SOURCE_CONFIG_UPDATE_FAILED: 'Failed to update source config. Please try again.',
  SOURCE_CONFIG_NO_CHANGES: 'Nothing to update — no fields were changed.',
  SOURCE_CONFIG_DELETED: 'Source config deleted successfully',
  SOURCE_CONFIG_DELETE_FAILED: 'Failed to delete source config. Please try again.',

  // Projects
  PROJECT_CREATED: 'Project added successfully',
  PROJECT_CREATE_FAILED: 'Failed to add project. Please try again.',
  PROJECT_UPDATED: 'Project updated successfully',
  PROJECT_UPDATE_FAILED: 'Failed to update project. Please try again.',
  PROJECT_NO_CHANGES: 'Nothing to update — no fields were changed.',

  // Roles
  ROLE_CREATED: 'Role added successfully',
  ROLE_CREATE_FAILED: 'Failed to add role. Please try again.',
  ROLE_UPDATED: 'Role updated successfully',
  ROLE_UPDATE_FAILED: 'Failed to update role. Please try again.',
  ROLE_DELETED: 'Role deleted successfully',
  ROLE_DELETE_FAILED: 'Failed to delete role. Please try again.',
  PERMISSIONS_LOAD_FAILED: 'Failed to load the permission list. Please try again.',

  // Files
  FILE_TYPE_UNSUPPORTED: 'Unsupported file type',
  FILE_SIZE_EXCEEDED: 'File size exceeds 5MB',
} as const;

// ── Card / Section Titles ──────────────────────────────────────────

export const SECTION_TITLES = {
  CLIENT_DETAILS: 'Client Details',
  ACCOUNT_PLAN: 'Account & Plan',
  PERSONAL_DETAILS: 'Personal Details',
  ACCOUNT_DETAILS: 'Account Details',
  STATISTICS_SUMMARY: 'Statistics Summary',
  INTEGRATION_INFO: 'Integration Information',
  TICKET_INFO: 'Ticket Information',
  PERSONAL_INFORMATION: 'Personal Information',
  DETAILS: 'Details',
  DESCRIPTION: 'Description',
  CONVERSATION: 'Conversation',
  ACTIVITY_LOG: 'Activity Log',
  CONFIGURATION: 'Configuration',
  SUBSCRIPTION_INFO: 'Subscription Information',
  TERM_DURATION: 'Term & Duration',
  INVOICE_INFO: 'Invoice Information',
  DATES_PAYMENT: 'Dates & Payment',
  EVENT_INFO: 'Event Information',
  PROJECT_INFO: 'Project Information',
  PROJECT_CONFIG: 'Project Config',
  RELATED_INFO: 'Related Information',
  REQUEST_PAYLOAD: 'Request Payload',
  RESPONSE_PAYLOAD: 'Response Payload',
  NOTIFICATIONS: 'Notifications',
  SECURITY: 'Security',
  GENERAL: 'General',
  INVOICES: 'Invoices',
  STATISTICS_LAST_7_DAYS: 'Statistics (Last 7 Days)',
  INTEGRATION_OVERVIEW: 'Integration Overview',
  SUPPORT_TICKETS: 'Support Tickets',
  RECENT_EVENTS: 'Recent Event Logs',
  SUBSCRIPTIONS_BILLING: 'Subscriptions & Billing',
  SUBSCRIPTION_DETAILS: 'Subscription Details',
  COUNT_OVERVIEW: 'Overview',
  ACTIVITY_TREND: 'Recent Activity Trend',
} as const;

// ── Stat Card Labels ───────────────────────────────────────────────

export const STAT_LABELS = {
  ACTIVE_USERS: 'Active Users',
  CUSTOMERS: 'Customers',
  USERS: 'Users',
  INTEGRATIONS: 'Integrations',
  TICKETS: 'Tickets',
  FOLLOW_UPS: 'Follow Ups',
  INVOICES: 'Invoices',
  EVENT_LOGS: 'Event Logs',
  TOTAL_CLIENTS: 'Total Clients',
  TOTAL_USERS: 'Total Users',
  TOTAL_INTEGRATIONS: 'Total Integrations',
  OPEN_TICKETS: 'Open Tickets',
  TOTAL_EVENTS: 'Total Events',
  SUCCESSFUL: 'Successful',
  FAILED: 'Failed',
  AVG_PROCESSING: 'Avg. Processing',
  RECENT_INTEGRATIONS: 'Integrations',
  RECENT_TICKETS_CREATED: 'Tickets Created',
  RECENT_EVENTS: 'Event Logs',
} as const;

// ── Stat Card Subtitles ────────────────────────────────────────────

export const STAT_SUBTITLES = {
  REGISTERED_CLIENTS: 'Registered clients',
  ACTIVE_USERS: 'Active users',
  CONNECTED_PLATFORMS: 'Connected platforms',
  NEEDS_ATTENTION: 'Needs attention',
} as const;

// ── Info Row Labels ────────────────────────────────────────────────

export const INFO_LABELS = {
  /** Precedes the name of whoever performed an action, e.g. "by Prasad Korgaonkar". */
  BY: 'by',
  /** Stands in when an action has no person behind it. */
  SYSTEM: 'System',
  NAME: 'Name',
  COMPANY: 'Company',
  EMAIL: 'Email',
  SLUG: 'Slug',
  CONTACT_PERSON: 'Contact Person',
  PHONE: 'Phone',
  WEBSITE: 'Website',
  ADDRESS: 'Address',
  STATUS: 'Status',
  PLAN: 'Plan',
  CURRENT_PLAN: 'Current Plan',
  ONBOARDING_DATE: 'Created At',
  TOTAL_USERS: 'Total Users',
  TOTAL_INTEGRATIONS: 'Total Integrations',
  FULL_NAME: 'Full Name',
  ROLE: 'Role',
  LAST_LOGIN: 'Last Login',
  CREATED_DATE: 'Created Date',
  USER_ID: 'User ID',
  PROVIDER: 'Provider',
  TENANT_ID: 'Tenant ID',
  CONNECTOR_ID: 'Connector ID',
  PURCHASED_AT: 'Purchased At',
  CONNECTED_AT: 'Connected At',
  ACTIVATED_AT: 'Activated At',
  EMAIL_ADDRESS: 'Email Address',
  COUNTRY: 'Country',
  MEMBER_SINCE: 'Member Since',
  INVOICE_NUMBER: 'Invoice Number',
  AMOUNT: 'Amount',
  CURRENCY: 'Currency',
  ISSUE_DATE: 'Issue Date',
  DUE_DATE: 'Due Date',
  PAID_AT: 'Paid At',
  SUBSCRIPTION_START_DATE: 'Subscription Start Date',
  SUBSCRIPTION_END_DATE: 'Subscription End Date',
  CLIENT_NAME: 'Client Name',
  ASSIGNED_USER: 'Assigned User',
  PLAN_NAME: 'Plan Name',
  BILLING_TYPE: 'Billing Type',
  PRICE: 'Price',
  DISCOUNT_PERCENT: 'Percent',
  START_DATE: 'Start Date',
  EXPIRY_DATE: 'Expiry Date',
  RENEWAL_DATE: 'Renewal Date',
  USER: 'User',
  PROJECT: 'Project',
  CLIENT: 'Client',
  DEACTIVATE_REASON: 'Reason for Inactive',
  POPOVER_DEACTIVATE_REASON: 'Reason Of Inactive',
  DEFAULT_DEACTIVATE_REASON: 'No reason provided',
  INTEGRATION_NAME: 'Integration',
  EVENT_TYPE: 'Event Type',
  TRIGGERED_AT: 'Triggered At',
  PROCESSING_TIME: 'Processing Time',
  CUSTOMER_NAME: 'Customer Name',
  TOTAL_AMOUNT: 'Total Amount',
  CREATED_BY: 'Created By',
  RAISED_BY: 'Raised By',
  ASSIGNED_TO: 'Assigned To',
  CREATED: 'Created',
  UPDATED: 'Updated',
  REASSIGN_REASON: 'Reassign Reason',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  SYNC_INTERVAL: 'Sync Interval',
  DATA_DIRECTION: 'Data Direction',
  AUTO_RETRY: 'Auto Retry',
  MAX_RETRIES: 'Max Retries',
  TIMEOUT: 'Timeout',
  WEBHOOK_URL: 'Webhook URL',
  CATEGORY: 'Category',
  AUTHENTICATOR_CODE: 'Authenticator Code',
  MANUAL_ENTRY_KEY: 'Manual Entry Key',
  USERS: 'Users',
  SELECT_USERS: 'Select users',
  SELECT_USER: 'Select user',
  CUSTOMERS: 'Customers',
  SELECT_CUSTOMERS: 'Select customers',
  CLIENT_USER: 'Client User',
  SELECT_CLIENT_FIRST: 'Select a client first',
  CONNECTED: 'Connected',
  VIEW_DETAILS: 'View Details',
} as const;

export const TWO_FA_STRINGS = {
  DISABLE_WARNING:
    'Disabling Two-Factor Authentication will reduce the security of your account. Verify your identity to continue.',
  COPY_KEY: 'Copy key',
  STEP_ONE_TITLE: 'Step 1: Scan QR Code',
  STEP_TWO_TITLE: 'Step 2: Enter Verification Code',
  MANUAL_KEY_DESCRIPTION:
    'Copy this key and paste it into your authenticator app (Google Authenticator, Authy, etc.) to set up 2FA.',
  ENTER_CODE_HINT: 'Enter the 6-digit code from your authenticator app',
  RETRY_COUNTDOWN: 'Try again in',
} as const;

// ── Credential Labels ──────────────────────────────────────────────

export const CREDENTIAL_LABELS = {
  INTEGRATION: 'Integration',
  CREDENTIAL_TYPE: 'Credential Type',
  USERNAME: 'Username',
  PASSWORD: 'Password',
  USERNAME_PASSWORD: 'Username & Password',
  OTHER: 'Other',
  CREDENTIAL: 'Credential',
  ADD_CREDENTIALS: 'Add Credentials',
  UPDATE_CREDENTIALS: 'Update Credentials',
  COPY_USERNAME: 'Copy username',
  COPY_PASSWORD: 'Copy password',
  SHOW_PASSWORD: 'Show password',
  HIDE_PASSWORD: 'Hide password',
  SHOW_VALUE: 'Show value',
  HIDE_VALUE: 'Hide value',
  VIEW_CREDENTIALS: 'View credentials',
  EDIT_CREDENTIALS: 'Edit Credentials',
} as const;

// ── Billing Labels ─────────────────────────────────────────────────

export const BILLING_LABELS: Record<string, string> = {
  ONE_TIME: 'One-Time',
  MONTHLY: 'Monthly',
  YEARLY: 'Yearly',
};

export const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '\u20B9',
  USD: '$',
  EUR: '\u20AC',
  GBP: '\u00A3',
};

// ── Validation Messages ────────────────────────────────────────────

export const VALIDATION = {
  // Required fields
  REQUIRED: (field: string) => `${field} is required`,
  SELECT_REQUIRED: (field: string) => `Please select ${field.toLowerCase()}`,

  // String constraints
  MIN_LENGTH: (field: string, min: number) => `${field} must be at least ${min} characters`,
  MAX_LENGTH: (field: string, max: number) => `${field} must be at most ${max} characters`,
  INVALID_EMAIL: 'Enter a valid email',

  // Number constraints
  MIN_VALUE: (field: string, min: number) => `${field} must be ${min} or more`,
  MAX_VALUE: (field: string, min: number) => `${field} must be ${min} or less`,

  // Password
  PASSWORD_MIN: 'Password must be at least 8 characters',
  PASSWORD_MAX: (max: number) => `Password must be at most ${max} characters`,
  PASSWORD_SPECIAL_CHAR: 'Password must contain at least one special character',
  PASSWORD_NUMERIC: 'Password must contain at least one number',
  PASSWORD_CONFIRM: 'Please confirm your password',
  PASSWORD_MISMATCH: 'Passwords must match',
  PASSWORD_UPPERCASE: 'Password must contain at least one uppercase letter',
  PASSWORD_LOWERCASE: 'Password must contain at least one lowercase letter',
} as const;

// ── Search Placeholders ────────────────────────────────────────────

export const SEARCH_PLACEHOLDERS = {
  CLIENTS: 'Search clients...',
  USERS: 'Search users...',
  TICKETS: 'Search tickets...',
  INTEGRATIONS: 'Search integrations...',
  EVENT_LOGS: 'Search event logs...',
  USER_SUBSCRIPTIONS: 'Search user subscriptions...',
  SUBSCRIPTIONS: 'Search subscriptions...',
} as const;

// ── Aria Labels ────────────────────────────────────────────────────

export const ARIA_LABELS = {
  EDIT_FOLLOW_UP: 'Edit Follow-up',
  FILTER_BY_STATUS: 'Filter by status',
  TOGGLE_CLIENT_STATUS: 'Activate or deactivate client',
  TOGGLE_USER_STATUS: 'Activate or deactivate user',
  FILTER_BY_CLIENT: 'Filter by client',
  FILTER_BY_ROLE: 'Filter by role',
  FILTER_BY_PROJECT: 'Filter by project',
  FILTER_BY_USER: 'Filter by user',
  FILTER_BY_PRIORITY: 'Filter by priority',
  FILTER_INTEGRATIONS_BY_USER: 'Filter integrations by user',
  FILTER_INVOICES_BY_USER: 'Filter invoices by user',
  FILTER_EVENT_LOGS_BY_USER: 'Filter event logs by user',
  UPDATE_TICKET_STATUS: 'Update ticket status',
  REASSIGN_TICKET: 'Reassign ticket',
  CLOSE_TICKET: 'Mark ticket as closed',
  GRID_VIEW: 'Grid view',
  LIST_VIEW: 'List view',
  UPGRADE_PLAN: 'Upgrade subscription plan',
  COPY_TO_CLIPBOARD: 'Copy to clipboard',
  COPIED_TO_CLIPBOARD: 'Copied to clipboard',
  COPY_PASSWORD: 'Copy password',
  DELETE_MESSAGE: 'Delete message',
  REMOVE_FILE: 'Remove file',
  ATTACH_FILES: 'Attach files',
  SHOW_PASSWORD: 'Show password',
  HIDE_PASSWORD: 'Hide password',
  EDIT_PLAN: 'Edit plan',
  VIEW_PDF: 'View PDF',
  INVOICE_PDF: 'Invoice PDF',
  SWITCH_TO_LIGHT: 'Switch to light mode',
  SWITCH_TO_DARK: 'Switch to dark mode',
  USER_MENU: 'User menu',
  TOGGLE_FILTERS: 'Toggle filters',
  FILTERS: 'Filters',
  FILTERS_APPLIED: 'Filters applied',
  ONBOARDED: 'Onboarded',
  NOT_ONBOARDED: 'Not onboarded',
  TOGGLE_SIDEBAR: 'Toggle sidebar',
  SIDEBAR: 'sidebar',
  COLLAPSE_SIDEBAR: 'Collapse sidebar',
  EXPAND_SIDEBAR: 'Expand sidebar',
  CLOSE_MODAL: 'Close dialog',
  MAKE_ACTIVE: 'Make active',
  MAKE_INACTIVE: 'Make inactive',
} as const;

// ── Modal Titles ───────────────────────────────────────────────────

export const MODAL_TITLES = {
  EDIT_PROFILE: 'Edit Profile',
  CHANGE_PASSWORD: 'Change Password',
  CREATE_SUPPORT_TICKET: 'Create Support Ticket',
  INVITE_USER: 'Invite User',
  CREATE_PLAN: 'Create Plan',
  EDIT_PLAN: 'Edit Plan',
  ASSIGN_PLAN: 'Assign Plan',
  UPGRADE_PLAN: 'Upgrade Plan',
  UPGRADE_USER_PLAN: 'Upgrade User Plan',
  DEACTIVATE_SUBSCRIPTION: 'Deactivate Subscription',
  REACTIVATE_SUBSCRIPTION: 'Reactivate Subscription',
  DISABLE_2FA: 'Disable Two-Factor Authentication',
  SETUP_2FA: 'Set up Two-Factor Authentication',
  SAVE_RECOVERY_CODES: 'Save Your Recovery Codes',
  SIGN_OUT: 'Sign out',
} as const;

// ── Login Page Strings ─────────────────────────────────────────────

export const LOGIN_STRINGS = {
  SIGN_IN_SUBTITLE: 'Enter your credentials to access your account',
  AUTH_CODE_SUBTITLE: 'Enter a code from your authenticator or recovery codes',
  SIGNING_IN: 'Signing in...',
  FORGOT_PASSWORD: 'Forgot password?',
  DONT_HAVE_ACCOUNT: "Don't have an account?",
  CONTACT_ADMIN: 'Contact your administrator',
  BRAND_DESCRIPTION:
    'Manage customer plans, subscriptions, integrations, event monitoring, and support from one place.',
  VERIFYING: 'Verifying...',
  VERIFY_CODE: 'Verify Code',
  USE_AUTH_CODE: 'Use authenticator code',
  USE_RECOVERY_CODE: 'Use a recovery code instead',
  BACK_TO_LOGIN: 'Back to Login',
  RECOVERY_CODE: 'Recovery Code',
  RECOVERY_CODE_PLACEHOLDER: '\u00b7\u00b7\u00b7\u00b7',
} as const;

// ── Dashboard Strings ──────────────────────────────────────────────

export const DASHBOARD_STRINGS = {
  WELCOME_BACK: 'Welcome back,',
  USER_SUBTITLE: "Here's a quick look at your account activity.",
  ADMIN_SUBTITLE: "Here's what's happening with your integrations today.",
  VIEW_ALL: 'View all',
} as const;

// ── Unauthorized Page Strings ──────────────────────────────────────

export const UNAUTHORIZED_STRINGS = {
  DESCRIPTION:
    "You don't have permission to access this page. Contact your administrator if you believe this is an error.",
} as const;

// ── Settings Page Strings ──────────────────────────────────────────

export const SETTINGS_STRINGS = {
  EMAIL_NOTIFICATIONS: 'Email Notifications',
  EMAIL_NOTIFICATIONS_DESC: 'Receive notifications via email',
  SLACK_NOTIFICATIONS: 'Slack Notifications',
  SLACK_NOTIFICATIONS_DESC: 'Receive notifications via Slack',
  IN_APP_NOTIFICATIONS: 'In-App Notifications',
  IN_APP_NOTIFICATIONS_DESC: 'Show notifications within the application',
  TWO_FACTOR_AUTH: 'Two-Factor Authentication',
  TWO_FACTOR_AUTH_DESC: 'Add an extra layer of security to your account',
  SESSION_TIMEOUT: 'Session Timeout (minutes)',
  IP_WHITELIST: 'IP Whitelist',
  IP_WHITELIST_PLACEHOLDER: 'Enter IP addresses, one per line',
  TIMEZONE: 'Timezone',
  LANGUAGE: 'Language',
  DATE_FORMAT: 'Date Format',
} as const;

// ── Conversation / Ticket Detail Strings ───────────────────────────

/** Labels for the tabs a detail page is split into, where the tab is not specific to one screen. */
export const TAB_LABELS = {
  OVERVIEW: 'Overview',
  LOGS: 'Logs',
} as const;

export const CONVERSATION_STRINGS = {
  NO_MESSAGES: 'No messages yet. Start the conversation below.',
  INTERNAL_NOTE: 'Internal Note',
  YOU: 'You',
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  CUSTOMER: 'Customer',
  SUPPORT_TEAM: 'Support Team',
  DELETE_MESSAGE_TITLE: 'Delete Message',
  DELETE_MESSAGE_CONFIRM: 'This message will be permanently removed from the conversation. This cannot be undone.',
  TYPE_INTERNAL_NOTE: 'Type an internal note...',
  TYPE_REPLY: 'Type a reply...',
  CONVERSATION: 'Conversation',
  TICKET_NUMBER: 'Ticket Number:',
  UNASSIGNED: 'Unassigned',
} as const;

// ── Ticket Action Strings ──────────────────────────────────────────

export const TICKET_ACTION_STRINGS = {
  UPDATE_STATUS_TITLE: 'Update Ticket Status',
  REASSIGN_TITLE: 'Reassign Ticket',
  CLOSE_TITLE: 'Mark Ticket as Closed',
  TICKET_LABEL: 'Ticket',
  CURRENT_STATUS_LABEL: 'Current Status',
  NEW_STATUS_LABEL: 'New Status',
  CURRENT_ASSIGNEE_LABEL: 'Currently Assigned To',
  REASSIGN_LABEL: 'Reassign To',
  REASSIGN_PLACEHOLDER: 'Select a user',
  REASSIGN_LOCKED_NOTICE: 'The ticket goes back to its current assignee — this cannot be changed.',
  REASSIGN_REASON_LABEL: 'Reason for Reassignment',
  REASSIGN_REASON_PLACEHOLDER: 'Why is this ticket being reassigned?',
  REASSIGN_RESOLVED_NOTICE: 'Reassigning a resolved ticket reopens it for further work.',
  RESOLVE_NOTICE: 'Marking this resolved hands it back for verification and closing.',
  CLOSE_CONFIRM: (ticket: string) =>
    `Please close ticket ${ticket} only after confirming that the issue has been fully resolved. Once closed, the ticket cannot be reopened or reassigned.`,
} as const;

// ── Source Config Strings ──────────────────────────────────────────

export const SOURCE_CONFIG_STRINGS = {
  PAGE_TITLE: 'Source Config',
  ADD_CONFIG: 'Add Config',
  ADD_MODAL_TITLE: 'Add Source Config',
  EDIT_MODAL_TITLE: 'Edit Source Config',
  // Tooltip / accessible names for the icon buttons in a table row.
  EDIT_ACTION: 'Edit source config',
  TEST_ACTION: 'Test connection',
  DELETE_ACTION: 'Delete source config',
  // Visible labels for the same actions as buttons on the detail page header.
  // TEST_LABEL is declared further down alongside the other connection-test strings.
  EDIT_LABEL: 'Edit',
  DELETE_LABEL: 'Delete',

  // Connection test — reported in a progress dialog rather than a toast, because the request
  // runs long enough that a transient toast is easy to miss.
  TEST_MODAL_TITLE: 'Test Connection',
  TEST_IN_PROGRESS: 'Testing the database connection. This can take a few moments…',
  TEST_SUCCESS: 'Connection succeeded.',
  TEST_FAILURE: 'Connection failed.',
  DELETE_MODAL_TITLE: 'Delete Source Config',
  DELETE_CONFIRM: (label: string) =>
    `Delete source config "${label}"? Any sync that depends on it will stop working. This cannot be undone.`,
  TEST_LABEL: 'Test Connection',
  NO_PROJECTS: 'No projects available. Every project already has a source config.',
  PLATFORM_WIDE: 'Platform-wide',
  NEVER_SYNCED: 'Never synced',
  LABEL: 'Label',
  PROJECT_ID: 'Project ID',
  HOST: 'Host',
  PORT: 'Port',
  DATABASE: 'Database',
  USERNAME: 'Username',
  PASSWORD: 'Password',
  // Labels the `enabled` flag; shown as "Active" to match the status pill.
  ACTIVE: 'Active',
  LAST_SYNCED: 'Last Synced',
  LAST_SYNC_STATUS: 'Last Sync Status',
  SEARCH_PLACEHOLDER: 'Search source configs...',

  // Detail page
  DETAIL_TITLE: 'Source Config Details',
  CONNECTION_SECTION: 'Connection',
  METADATA_SECTION: 'Configuration',
  SOURCE_ID: 'Source ID',
  DRIVER: 'Driver',
  JDBC_URL: 'JDBC URL',
  AUTO_DETECTED: 'Auto-detected',
  REVEAL_PASSWORD: 'Reveal password',
  HIDE_PASSWORD: 'Hide password',
  COPY_PASSWORD: 'Copy password',
  COPY_JDBC_URL: 'Copy JDBC URL',
  NOT_FOUND: 'Source config not found.',
  BACK_TO_LIST: 'Back to Source Config',
} as const;

// ── Project Strings ────────────────────────────────────────────────

export const USER_STRINGS = {
  EDIT_PAGE_TITLE: 'Edit User',
  EDIT_ACTION: 'Edit user',
} as const;

export const CLIENT_STRINGS = {
  EDIT_MODAL_TITLE: 'Edit Client',
  EDIT_ACTION: 'Edit client',
  EDIT_LABEL: 'Edit',
} as const;

export const PROJECT_STRINGS = {
  PAGE_TITLE: 'Project Management',
  ADD_PROJECT: 'Add Project',
  ADD_MODAL_TITLE: 'Add Project',
  EDIT_MODAL_TITLE: 'Edit Project',
  EDIT_ACTION: 'Edit project',
  EDIT_LABEL: 'Edit',

  // Fields
  TYPE: 'Type',
  INTEGRATIONS: 'Integrations',
  SYNC: 'Sync',
  BILLING: 'Billing',
  ACTIVE: 'Active',
  SEARCH_PLACEHOLDER: 'Search projects...',

  SELECT_PLACEHOLDER: 'Select a project',
  NONE_FOUND: 'No projects found',
  LOAD_FAILED: 'Could not load projects',

  // Detail page
  DETAIL_TITLE: 'Project Details',
  NOT_FOUND: 'Project not found.',
  BACK_TO_LIST: 'Back to Projects',
} as const;

// ── Create Ticket Strings ──────────────────────────────────────────

export const CREATE_TICKET_STRINGS = {
  SELECT_CLIENT: 'Select a client',
  SELECT_USER: 'Select a user',
  SELECT_INTEGRATION: 'Select an integration',
  SELECT_CATEGORY: 'Select a category',
} as const;

// ── Create User Strings ────────────────────────────────────────────

export const CREATE_USER_STRINGS = {
  STEP_ONE_LABEL: 'User type',
  STEP_TWO_LABEL: 'User details',

  // Step 1 — who the user is
  TYPE_TITLE: 'What kind of user is this?',
  TYPE_SUBTITLE: 'This decides which company they belong to and how many projects they can work across.',
  INTERNAL_TITLE: 'Internal User',
  INTERNAL_DESCRIPTION: 'Works for your own company, across one or more projects.',
  EXTERNAL_TITLE: 'External User',
  EXTERNAL_DESCRIPTION: 'Belongs to a client company, on a single project.',
  TYPE_REQUIRED: 'Choose a user type to continue.',
  CONTINUE: 'Continue',

  // Step 2 — the form
  DETAILS_TITLE: 'User details',
  CLIENT: 'Select Client',
  CLIENT_PLACEHOLDER: 'Select a client',
  NAME: 'User Name',
  NAME_PLACEHOLDER: 'Name',
  EMAIL: 'Email',
  EMAIL_PLACEHOLDER: 'Email',
  COUNTRY: 'Country',
  COUNTRY_PLACEHOLDER: 'Select a country',
  ROLE: 'Select Role',
  ROLE_PLACEHOLDER: 'Select a role',
  ROLE_NONE: 'No roles found',
  STATUS: 'Status',
  STATUS_PLACEHOLDER: 'Select a status',
  PROJECTS: 'Select Projects',
  PROJECTS_PLACEHOLDER: 'Select one or more projects',
  PROJECTS_REQUIRED: 'Select at least one project',
  PROJECT: 'Select Project',
  PROJECT_PLACEHOLDER: 'Select a project',
  PROJECT_NONE: 'No projects found',
} as const;

// ── Role Management Strings ────────────────────────────────────────

export const ROLE_STRINGS = {
  PAGE_TITLE: 'Role Management',
  ADD_ROLE: 'Add Role',
  ADD_MODAL_TITLE: 'Add Role',
  EDIT_MODAL_TITLE: 'Edit Role',
  EDIT_ACTION: 'Edit role',
  DELETE_ACTION: 'Delete role',
  DELETE_MODAL_TITLE: 'Delete Role',
  DELETE_CONFIRM: (name: string) =>
    `Delete role "${name}"? Users holding it will lose the permissions it grants. This cannot be undone.`,

  // Columns
  PERMISSIONS: 'Permissions',
  PERMISSION_COUNT: (count: number) => `${count} ${count === 1 ? 'permission' : 'permissions'}`,
  NO_PERMISSIONS: 'No permissions',
  SEARCH_PLACEHOLDER: 'Search roles or permissions...',

  // Modal
  PERMISSIONS_LABEL: 'Permissions',
  PERMISSIONS_REQUIRED: 'Select at least one permission',
  PERMISSIONS_EMPTY: 'No permissions are available.',
  PERMISSIONS_SELECTED: (selected: number, total: number) => `${selected} of ${total} selected`,
  EDIT_NOTE: 'Only permissions can be changed on an existing role.',
} as const;

// ── Role Labels ────────────────────────────────────────────────────

export const ROLE_LABELS: Record<string, string> = {
  SUPERADMIN: 'Super Admin',
  ADMIN: 'Admin',
  USER: 'User',
  ACCOUNTANT: 'Accountant',
};

// ── Status Labels ─────────────────────────────────────────────────

export const STATUS_LABELS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  PENDING: 'Pending',
  PAID: 'Paid',
} as const;

// ── Date Formats & Placeholders ────────────────────────────────────

export const DATE_FORMATS = {
  MMM_DD: 'MMM dd',
} as const;

export const CHART_LABELS = {
  COUNT: 'Count',
} as const;

export const DATE_PLACEHOLDERS = {
  ONBOARDING_DATE: 'Created At',
  TRIGGERED_DATE: 'Triggered Date',
} as const;

export const DATE_PICKER_FORMAT = 'dd/MM/yyyy';
export const DATE_PICKER_TIME_FORMAT = 'dd/MM/yyyy hh:mm aa';

export const DATE_PICKER_SHORTCUTS = {
  TODAY: 'Today',
  YESTERDAY: 'Yesterday',
} as const;

// ── Filter Labels ──────────────────────────────────────────────────

export const FILTER_LABELS = {
  ALL_CLIENTS: 'All Clients',
  ALL_USERS: 'All Users',
  ALL_STATUS: 'All Status',
  ALL_ROLES: 'All Roles',
  ALL_PROJECTS: 'All Projects',
} as const;

// ── Follow-up Type Labels ──────────────────────────────────────────

export const FOLLOW_UP_TYPE_LABELS: Record<string, string> = {
  email: 'Email',
  phone_call: 'Phone Call',
  whatsapp: 'WhatsApp',
  google_meet: 'Google Meet',
  other: 'Other',
};

// ── Breadcrumb Labels ──────────────────────────────────────────────

export const BREADCRUMB_LABELS = {
  CLIENTS: 'Clients',
  CLIENT: 'Client',
  USERS: 'Users',
  TICKETS: 'Tickets',
  INTEGRATIONS: 'Integrations',
  EVENT_LOGS: 'Event Logs',
} as const;

export const STATUS_CONFIRM = {
  ACTIVATE_TITLE: 'Activate',
  DEACTIVATE_TITLE: 'Deactivate',
  ACTIVATE_CLIENT: (name: string) => `Activate ${name}? Its users will be able to sign in again.`,
  DEACTIVATE_CLIENT: (name: string) => `Deactivate ${name}? Its users will lose access until it is activated again.`,
  ACTIVATE_USER: (name: string) => `Activate ${name}? They will be able to sign in again.`,
  DEACTIVATE_USER: (name: string) => `Deactivate ${name}? They will lose access until they are activated again.`,
} as const;

// ── Modal Labels ──────────────────────────────────────────────

export const MODAL_STRINGS = {
  DANGER: 'danger',
  SIGN_OUT_CONFIRMATION: 'Are you sure you want to sign out?',
  DEACTIVATE_CONFIRMATION: 'Are you sure you want to deactivate this subscription? Access and billing will be paused.',
  REACTIVATE_CONFIRMATION: 'Are you sure you want to reactivate this subscription? Access and billing will be resumed.',
  DEACTIVATE_REASON_PLACEHOLDER: 'Enter the reason for deactivating this subscription...',
} as const;

// ── Subscription Strings ──────────────────────────────────────────────

export const SUBSCRIPTION_STRINGS = {
  INR: 'INR',
  MONTHLY: 'MONTHLY',
  CLIENT_PLAN: 'Client Plan',
  SAME_AS_CLIENT: 'Same as client',
  CURRENT_PLAN: 'Current Plan',
  CONTINUE_WITH_CLIENT: 'Continue with the client',
  CUSTOM_AMOUNT: 'Custom Amount',
  CUSTOM: 'Custom',
  SET_OWN_PRICE: 'Set your own price',
  ONE_TIME: 'ONE_TIME',
  NO_SUBSCRIPTIONS_AVAILABLE: 'No subscriptions available.',
  INACTIVE_SUBSCRIPTION_SUFFIX: ' \u2014 Inactive Subscription',
  SUBSCRIBED_PREFIX: ' \u2014 Subscribed:',
} as const;

// ── Credential Strings ──────────────────────────────────────────────

export const CREDENTIAL_STRINGS = {
  COPIED: 'Copied!',
  COPY_USERNAME: 'Copy Username',
  COPY_PASSWORD: 'Copy Password',
  REMOVE_FIELD: 'Remove Field',
} as const;

// ── Footer Strings ──────────────────────────────────────────────────

export const FOOTER_STRINGS = {
  COPYRIGHT: (year: number) => `© ${year} Multigenesys. All rights reserved.`,
  DEVELOPED_BY: 'Software designed & developed by',
  COMPANY_DOMAIN: 'multigenesys.com',
  COMPANY_URL: 'https://multigenesys.com',
} as const;
